import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Shield, Search, AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronUp, Clock, Zap, BookOpen, Send, Loader2, Heart, FileText, Database, Brain, ShieldCheck, ArrowRight, Sparkles, ExternalLink, Cpu, Layers, ThumbsUp, ThumbsDown, MessageCircle, Activity, GitCompare, Columns, Volume2, VolumeX, Square, Mic, MicOff } from 'lucide-react';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ─── Inline Citation Badge ──────────────────────────────────────────
function CitationBadge({ sources }) {
  const [show, setShow] = useState(false);
  if (!sources || sources.length === 0) return null;
  const primary = sources[0];
  const extra = sources.length - 1;
  return (
    <span className="cite-badge-wrap" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <span className="cite-badge">
        <span className="cite-icon">{primary.source_type === 'fda' ? '🏛' : '📄'}</span>
        <span className="cite-label">{primary.source_type?.toUpperCase()}</span>
        {extra > 0 && <span className="cite-extra">+{extra}</span>}
      </span>
      {show && (
        <div className="cite-tooltip">
          {sources.map((s, i) => (
            <div key={i} className="cite-tooltip-item">
              <span className="cite-tooltip-type">{s.source_type?.toUpperCase()}</span>
              <span className="cite-tooltip-title">{s.title}</span>
              {s.url && <a href={s.url} target="_blank" rel="noreferrer"><ExternalLink size={11} /></a>}
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

// ─── Listen Button (ElevenLabs TTS) ─────────────────────────────────
function ListenButton({ text }) {
  const [state, setState] = useState('idle'); // idle | loading | playing
  const audioRef = useRef(null);

  const handleClick = async () => {
    // If playing, stop
    if (state === 'playing' && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setState('idle');
      return;
    }

    setState('loading');
    try {
      const resp = await fetch(`${API_URL}/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (!resp.ok) throw new Error(`TTS failed: ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);

      // Clean up previous audio
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }

      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setState('idle');
      audio.onerror = () => setState('idle');
      await audio.play();
      setState('playing');
    } catch (e) {
      console.error('TTS error:', e);
      setState('idle');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
    };
  }, []);

  return (
    <button
      className={`listen-btn ${state}`}
      onClick={handleClick}
      title={state === 'playing' ? 'Stop listening' : 'Listen to answer'}
      disabled={state === 'loading'}
    >
      {state === 'loading' && <Loader2 size={14} className="spinning" />}
      {state === 'playing' && <Square size={14} />}
      {state === 'idle' && <Volume2 size={14} />}
      <span>{state === 'loading' ? 'Generating...' : state === 'playing' ? 'Stop' : 'Listen'}</span>
    </button>
  );
}

// ─── Voice Input Button (Speech-to-Text) ─────────────────────────────
function VoiceInputButton({ onTranscript, disabled }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const supported = typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const toggle = useCallback(() => {
    if (!supported) return;

    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(r => r[0].transcript)
        .join('');
      onTranscript(transcript, event.results[0]?.isFinal);
    };

    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, [listening, supported, onTranscript]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  if (!supported) return null;

  return (
    <button
      type="button"
      className={`voice-input-btn ${listening ? 'recording' : ''}`}
      onClick={toggle}
      disabled={disabled}
      title={listening ? 'Stop recording' : 'Speak your question'}
    >
      {listening ? <MicOff size={18} /> : <Mic size={18} />}
    </button>
  );
}

// ─── Thinking Section (collapsible) ─────────────────────────────────
function ThinkingSection({ trace, isLoading, currentAgent }) {
  const [expanded, setExpanded] = useState(false);
  const agents = [
    { name: 'Retrieval Agent', icon: '🔍', desc: 'Searching PubMed & FDA databases' },
    { name: 'Reasoning Agent', icon: '🧠', desc: 'Generating evidence-based answer' },
    { name: 'Hallucination Detector', icon: '🛡️', desc: 'Running 8-layer verification checks' },
    { name: 'Safety Agent', icon: '⚕️', desc: 'Assigning confidence tiers' },
    { name: 'Output Agent', icon: '📋', desc: 'Formatting final response' },
  ];
  const currentIdx = agents.findIndex(a => a.name === currentAgent);

  return (
    <div className="thinking-section">
      <button className="thinking-toggle" onClick={() => setExpanded(!expanded)}>
        <ChevronDown size={16} className={`thinking-chevron ${expanded ? 'rotated' : ''}`} />
        <span>{isLoading ? 'Thinking...' : 'Finished thinking'}</span>
        {isLoading && <Loader2 size={14} className="spinning" />}
      </button>
      {expanded && (
        <div className="thinking-steps">
          {agents.map((agent, i) => {
            const traceStep = trace?.find(t => t.agent_name === agent.name);
            const isDone = !!traceStep || (!isLoading && trace?.length > 0);
            const isActive = isLoading && i === currentIdx;
            const isPending = isLoading && (currentIdx < 0 ? true : i > currentIdx);
            return (
              <div key={i} className={`thinking-step ${isDone ? 'done' : isActive ? 'active' : isPending ? 'pending' : ''}`}>
                <span className="step-icon">{agent.icon}</span>
                <div className="step-info">
                  <span className="step-name">{agent.name}</span>
                  <span className="step-desc">{agent.desc}</span>
                </div>
                <span className="step-status">
                  {isDone && traceStep && <span className="step-time">{(traceStep.duration_ms / 1000).toFixed(1)}s</span>}
                  {isDone && <CheckCircle size={14} className="step-check" />}
                  {isActive && <Loader2 size={14} className="spinning" />}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Answer Content Card ────────────────────────────────────────────
function AnswerCard({ result }) {
  const tierConfig = {
    green: { label: 'Drug Label — Verified', color: '#e87b35', bg: 'rgba(232,123,53,0.15)', border: 'rgba(232,123,53,0.4)' },
    yellow: { label: 'Drug Label — Partially Verified', color: '#e8a435', bg: 'rgba(232,164,53,0.15)', border: 'rgba(232,164,53,0.4)' },
    red: { label: 'Drug Label — Low Confidence', color: '#ef4444', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.4)' },
  };
  const tc = tierConfig[result.overall_confidence] || tierConfig.yellow;

  return (
    <div className="answer-card" style={{ borderTopColor: tc.border }}>
      <div className="answer-header" style={{ background: tc.bg, borderColor: tc.border }}>
        <Database size={16} />
        <span>{tc.label}</span>
      </div>
      <div className="answer-body">
        <p className="answer-text">{result.answer}</p>
        <ListenButton text={result.answer} />
      </div>
      <div className="answer-source-footer">
        <div className="source-org">
          <span className="source-org-icon">🏛</span>
          <div>
            <div className="source-org-name">PubMed & FDA Drug Labels</div>
            <div className="source-org-count">{result.claims?.reduce((acc, c) => acc + (c.sources?.length || 0), 0) || 0} sources referenced</div>
          </div>
        </div>
        <button className="source-expand-btn">
          {result.claims?.reduce((acc, c) => acc + (c.sources?.length || 0), 0) || 0} Sources <ChevronDown size={14} />
        </button>
      </div>
    </div>
  );
}

// ─── Claim Verification Card ────────────────────────────────────────
function ClaimCard({ claim, index }) {
  const [expanded, setExpanded] = useState(false);
  const score = Math.round(claim.confidence_score * 100);
  const tierColors = { green: '#10b981', yellow: '#e8a435', red: '#ef4444' };
  const color = tierColors[claim.confidence_tier] || tierColors.yellow;
  const tierLabels = { green: 'Verified', yellow: 'Needs Review', red: 'Unverified' };

  return (
    <div className={`claim-card tier-${claim.confidence_tier}`}>
      <div className="claim-header" onClick={() => setExpanded(!expanded)}>
        <div className="claim-left">
          <div className="claim-tier-dot" style={{ background: color }} />
          <span className="claim-tier-label" style={{ color }}>{tierLabels[claim.confidence_tier]}</span>
          <span className="claim-score" style={{ color }}>{score}%</span>
        </div>
        <ChevronDown size={15} className={`claim-chevron ${expanded ? 'rotated' : ''}`} />
      </div>
      <p className="claim-text">{claim.hedged_text || claim.text}</p>

      {expanded && (
        <div className="claim-details">
          {claim.confidence_tier !== 'green' && claim.hedged_text && (
            <div className="original-text">
              <FileText size={13} />
              <div><strong>Original claim:</strong> {claim.text}</div>
            </div>
          )}
          <div className="verification-checks">
            <div className="v-check">
              <div className="v-check-header">
                <span className="v-check-name">NLI Entailment</span>
                <span className="v-check-score" style={{ color }}>{Math.round(claim.entailment_score * 100)}%</span>
              </div>
              <div className="v-check-bar-bg"><div className="v-check-bar" style={{ width: `${claim.entailment_score * 100}%`, background: color }} /></div>
            </div>
            <div className="v-check">
              <div className="v-check-header">
                <span className="v-check-name">Entity Match</span>
                <span className={`v-check-label ${claim.entity_consistent ? 'pass' : 'fail'}`}>
                  {claim.entity_consistent ? 'Consistent' : 'Mismatch'}
                </span>
              </div>
            </div>
            <div className="v-check">
              <div className="v-check-header">
                <span className="v-check-name">Self-Consistency</span>
                <span className="v-check-score" style={{ color }}>{Math.round(claim.self_consistency_score * 100)}%</span>
              </div>
              <div className="v-check-bar-bg"><div className="v-check-bar" style={{ width: `${claim.self_consistency_score * 100}%`, background: color }} /></div>
            </div>
            <div className="v-check">
              <div className="v-check-header">
                <span className="v-check-name">Source Attribution</span>
                <span className="v-check-score" style={{ color }}>{Math.round((claim.source_attribution_score || 0.5) * 100)}%</span>
              </div>
              <div className="v-check-bar-bg"><div className="v-check-bar" style={{ width: `${(claim.source_attribution_score || 0.5) * 100}%`, background: color }} /></div>
            </div>
            <div className="v-check">
              <div className="v-check-header">
                <span className="v-check-name">Temporal Validity</span>
                <span className={`v-check-label ${claim.temporally_valid !== false ? 'pass' : 'fail'}`}>
                  {claim.temporally_valid !== false ? 'Current' : 'Outdated'}
                </span>
              </div>
            </div>
            <div className="v-check">
              <div className="v-check-header">
                <span className="v-check-name">Cross-Claim</span>
                <span className="v-check-score" style={{ color }}>{Math.round((claim.cross_claim_score || 1.0) * 100)}%</span>
              </div>
              <div className="v-check-bar-bg"><div className="v-check-bar" style={{ width: `${(claim.cross_claim_score || 1.0) * 100}%`, background: color }} /></div>
            </div>
            <div className="v-check">
              <div className="v-check-header">
                <span className="v-check-name">Faithfulness</span>
                <span className="v-check-score" style={{ color }}>{Math.round((claim.faithfulness_score || 0.5) * 100)}%</span>
              </div>
              <div className="v-check-bar-bg"><div className="v-check-bar" style={{ width: `${(claim.faithfulness_score || 0.5) * 100}%`, background: color }} /></div>
              {claim.verification_details?.faithfulness_total > 0 && (
                <div className="v-check-detail">{claim.verification_details.faithfulness_supported}/{claim.verification_details.faithfulness_total} atomic facts verified</div>
              )}
            </div>
            <div className="v-check">
              <div className="v-check-header">
                <span className="v-check-name">RAG Utilization</span>
                <span className="v-check-score" style={{ color }}>{Math.round((claim.retrieval_utilization_score || 0.5) * 100)}%</span>
              </div>
              <div className="v-check-bar-bg"><div className="v-check-bar" style={{ width: `${(claim.retrieval_utilization_score || 0.5) * 100}%`, background: color }} /></div>
              {claim.verification_details?.retrieval_precision != null && (
                <div className="v-check-detail">P: {Math.round(claim.verification_details.retrieval_precision * 100)}% · R: {Math.round(claim.verification_details.retrieval_recall * 100)}%</div>
              )}
            </div>
          </div>
          {claim.sources?.length > 0 && (
            <div className="claim-sources">
              {claim.sources.map((src, i) => (
                <div key={i} className="claim-source-item">
                  <span className="cs-type">{src.source_type?.toUpperCase()}</span>
                  <span className="cs-title">{src.title}</span>
                  {src.url && <a href={src.url} target="_blank" rel="noreferrer"><ExternalLink size={11} /></a>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── References Section ─────────────────────────────────────────────
function ReferencesSection({ claims }) {
  const [expanded, setExpanded] = useState(true);
  const allSources = [];
  const seen = new Set();
  claims?.forEach(c => {
    c.sources?.forEach(s => {
      const key = s.title + s.source_type;
      if (!seen.has(key)) { seen.add(key); allSources.push(s); }
    });
  });
  if (allSources.length === 0) return null;

  return (
    <div className="references-section">
      <button className="references-toggle" onClick={() => setExpanded(!expanded)}>
        <BookOpen size={16} />
        <span>References</span>
        <ChevronDown size={16} className={`ref-chevron ${expanded ? '' : 'rotated-down'}`} />
      </button>
      {expanded && (
        <div className="references-list">
          {allSources.map((src, i) => (
            <div key={i} className="reference-item">
              <span className="ref-number">{i + 1}.</span>
              <div className="ref-content">
                <span className="ref-title">{src.title}. </span>
                {src.url ? (
                  <a href={src.url} target="_blank" rel="noreferrer" className="ref-link">{src.source_type?.toUpperCase()} Source.</a>
                ) : (
                  <span className="ref-type">{src.source_type?.toUpperCase()} Source.</span>
                )}
                <div className="ref-org">
                  <span className="ref-org-badge">{src.source_type === 'fda' ? '🏛' : '📄'}</span>
                  <span>{src.source_type === 'fda' ? 'Food and Drug Administration' : 'PubMed'}.</span>
                </div>
              </div>
              <div className="ref-feedback">
                <button className="ref-fb-btn"><ThumbsUp size={12} /></button>
                <button className="ref-fb-btn"><ThumbsDown size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Raw LLM Card (for compare mode) ────────────────────────────────
function RawLLMCard({ rawData }) {
  return (
    <div className="raw-llm-card">
      <div className="raw-llm-header">
        <AlertTriangle size={16} />
        <span>Raw LLM — No Verification</span>
      </div>
      <div className="raw-llm-warning">
        <XCircle size={14} />
        <span>{rawData.note}</span>
      </div>
      <div className="raw-llm-body">
        <p className="raw-llm-text">{rawData.answer}</p>
      </div>
      <div className="raw-llm-footer">
        <span className="raw-llm-model">{rawData.model}</span>
        <div className="raw-llm-missing">
          <span className="missing-badge">No Sources</span>
          <span className="missing-badge">No Fact-Check</span>
          <span className="missing-badge">No Confidence Score</span>
        </div>
      </div>
    </div>
  );
}

// ─── Single Conversation Turn ────────────────────────────────────────
function ConversationTurn({ turn, isLatest, isLoading, currentAgent }) {
  const overallMsg = {
    green: { icon: CheckCircle, text: 'High confidence — all claims verified against sources', color: '#10b981' },
    yellow: { icon: AlertTriangle, text: 'Some claims could not be fully verified — review flagged items', color: '#e8a435' },
    red: { icon: XCircle, text: 'Low confidence — multiple claims failed verification. Consult a healthcare professional.', color: '#ef4444' },
  };
  const result = turn.result;
  const om = overallMsg[result?.overall_confidence] || overallMsg.yellow;

  return (
    <div className={`turn ${isLatest ? 'turn-latest' : 'turn-past'}`}>
      {/* User bubble */}
      <div className="user-bubble">
        <div className="user-avatar-ring" />
        <div className="user-msg">{turn.question}</div>
      </div>

      {/* Thinking */}
      {isLatest && <ThinkingSection trace={result?.agent_trace} isLoading={isLoading && !result} currentAgent={currentAgent} />}
      {!isLatest && result && (
        <div className="thinking-section">
          <button className="thinking-toggle" disabled>
            <CheckCircle size={14} className="step-check" />
            <span>Finished thinking · {((result.agent_trace?.reduce((s, t) => s + t.duration_ms, 0) || 0) / 1000).toFixed(1)}s</span>
          </button>
        </div>
      )}

      {/* Result */}
      {result && (
        <>
          <div className={`overall-banner tier-${result.overall_confidence}`}>
            {React.createElement(om.icon, { size: 18, color: om.color })}
            <span style={{ color: om.color }}>{om.text}</span>
            {result.should_defer_to_professional && (
              <span className="defer-tag"><Heart size={12} /> Consult a doctor</span>
            )}
          </div>

          <AnswerCard result={result} />

          <div className="verification-section">
            <div className="v-section-header">
              <ShieldCheck size={16} />
              <span>Claim Verification</span>
              <span className="v-count">{result.claims?.length || 0} claims analyzed</span>
            </div>
            <div className="v-summary">
              <div className="v-sum-item green"><CheckCircle size={14} /><span>{result.claims?.filter(c => c.confidence_tier === 'green').length || 0} Verified</span></div>
              <div className="v-sum-item yellow"><AlertTriangle size={14} /><span>{result.claims?.filter(c => c.confidence_tier === 'yellow').length || 0} Uncertain</span></div>
              <div className="v-sum-item red"><XCircle size={14} /><span>{result.claims?.filter(c => c.confidence_tier === 'red').length || 0} Flagged</span></div>
              <div className="v-sum-item time"><Clock size={14} /><span>{((result.agent_trace?.reduce((s, t) => s + t.duration_ms, 0) || 0) / 1000).toFixed(1)}s</span></div>
            </div>
            {/* Only show claims expanded for latest turn, collapsed for past */}
            {isLatest ? (
              <div className="claims-list">
                {result.claims?.map((claim, i) => <ClaimCard key={i} claim={claim} index={i} />)}
              </div>
            ) : (
              <CollapsibleClaims claims={result.claims} />
            )}
          </div>

          <ReferencesSection claims={result.claims} />

          {isLatest && result.disclaimer && (
            <div className="disclaimer">
              <Shield size={13} /> {result.disclaimer}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Collapsible Claims (for older turns) ────────────────────────────
function CollapsibleClaims({ claims }) {
  const [show, setShow] = useState(false);
  if (!claims || claims.length === 0) return null;
  return (
    <div className="collapsible-claims">
      <button className="collapse-claims-btn" onClick={() => setShow(!show)}>
        <ChevronDown size={14} className={show ? 'rotated' : ''} />
        {show ? 'Hide' : 'Show'} {claims.length} claims
      </button>
      {show && (
        <div className="claims-list">
          {claims.map((claim, i) => <ClaimCard key={i} claim={claim} index={i} />)}
        </div>
      )}
    </div>
  );
}

// ─── Compare Turn (for compare mode history) ─────────────────────────
function CompareTurn({ turn, isLatest }) {
  const result = turn.result;
  const compareResult = turn.compareResult;
  const overallMsg = {
    green: { icon: CheckCircle, text: 'High confidence — all claims verified', color: '#10b981' },
    yellow: { icon: AlertTriangle, text: 'Some claims need review', color: '#e8a435' },
    red: { icon: XCircle, text: 'Low confidence — consult a professional', color: '#ef4444' },
  };
  const om = overallMsg[result?.overall_confidence] || overallMsg.yellow;

  return (
    <div className={`turn ${isLatest ? 'turn-latest' : 'turn-past'}`}>
      <div className="user-bubble compare-user-bubble">
        <div className="user-avatar-ring" />
        <div className="user-msg">{turn.question}</div>
      </div>

      <div className="compare-grid">
        {/* LEFT: Raw LLM */}
        <div className="compare-col compare-col-raw">
          <div className="compare-col-header raw">
            <AlertTriangle size={16} />
            <span>Raw LLM (No Safeguards)</span>
          </div>
          {compareResult?.raw_llm && <RawLLMCard rawData={compareResult.raw_llm} />}
        </div>

        {/* RIGHT: MedGuard */}
        <div className="compare-col compare-col-mg">
          <div className="compare-col-header mg">
            <ShieldCheck size={16} />
            <span>MedGuard (Verified)</span>
          </div>
          {result && (
            <>
              <div className={`overall-banner tier-${result.overall_confidence}`}>
                {React.createElement(om.icon, { size: 18, color: om.color })}
                <span style={{ color: om.color }}>{om.text}</span>
              </div>
              <AnswerCard result={result} />
              <div className="verification-section">
                <div className="v-section-header">
                  <ShieldCheck size={16} />
                  <span>Claim Verification</span>
                  <span className="v-count">{result.claims?.length || 0} claims</span>
                </div>
                <div className="v-summary">
                  <div className="v-sum-item green"><CheckCircle size={14} /><span>{result.claims?.filter(c => c.confidence_tier === 'green').length || 0} Verified</span></div>
                  <div className="v-sum-item yellow"><AlertTriangle size={14} /><span>{result.claims?.filter(c => c.confidence_tier === 'yellow').length || 0} Uncertain</span></div>
                  <div className="v-sum-item red"><XCircle size={14} /><span>{result.claims?.filter(c => c.confidence_tier === 'red').length || 0} Flagged</span></div>
                </div>
                {isLatest ? (
                  <div className="claims-list">
                    {result.claims?.map((claim, i) => <ClaimCard key={i} claim={claim} index={i} />)}
                  </div>
                ) : (
                  <CollapsibleClaims claims={result.claims} />
                )}
              </div>
              <ReferencesSection claims={result.claims} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main App ───────────────────────────────────────────────────────
export default function App() {
  const [query, setQuery] = useState('');
  // conversation = array of { question, result, compareResult }
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAgent, setCurrentAgent] = useState('');
  const [error, setError] = useState('');
  const [demoQuestions, setDemoQuestions] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const inputRef = useRef(null);
  const bottomRef = useRef(null);
  const followupRef = useRef(null);

  const handleVoiceTranscript = useCallback((transcript, isFinal) => {
    setQuery(transcript);
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/demo-questions`).then(r => r.json()).then(d => setDemoQuestions(d.questions || [])).catch(() => {});
  }, []);

  // Scroll to bottom when new turn added or result arrives
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [conversation, loading]);

  // Focus followup input after result comes in
  useEffect(() => {
    if (!loading && conversation.length > 0 && followupRef.current) {
      followupRef.current.focus();
    }
  }, [loading, conversation.length]);

  const handleSubmit = useCallback(async (questionText) => {
    const q = questionText || query;
    if (!q.trim()) return;

    // Add the new turn immediately (with no result yet)
    const turnIndex = conversation.length;
    setConversation(prev => [...prev, { question: q, result: null, compareResult: null }]);
    setQuery('');
    setLoading(true); setError(''); setCurrentAgent('');

    if (compareMode) {
      try {
        setCurrentAgent('Running both pipelines...');
        const resp = await fetch(`${API_URL}/query/compare`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ question: q }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        setConversation(prev => {
          const updated = [...prev];
          updated[turnIndex] = { ...updated[turnIndex], result: data.medguard, compareResult: data };
          return updated;
        });
      } catch (e) { setError(`Compare failed: ${e.message}`); }
      finally { setLoading(false); setCurrentAgent(''); }
    } else {
      try {
        const response = await fetch(`${API_URL}/query/stream`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ question: q }),
        });
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data === '[DONE]') continue;
              try {
                const parsed = JSON.parse(data);
                if (parsed.type === 'progress') setCurrentAgent(parsed.agent);
                else if (parsed.type === 'result') {
                  setConversation(prev => {
                    const updated = [...prev];
                    updated[turnIndex] = { ...updated[turnIndex], result: parsed.data };
                    return updated;
                  });
                }
              } catch {}
            }
          }
        }
      } catch {
        try {
          setCurrentAgent('Processing...');
          const resp = await fetch(`${API_URL}/query`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ question: q }),
          });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const data = await resp.json();
          setConversation(prev => {
            const updated = [...prev];
            updated[turnIndex] = { ...updated[turnIndex], result: data };
            return updated;
          });
        } catch (e) { setError(`Cannot reach MedGuard backend at ${API_URL}. ${e.message}`); }
      } finally { setLoading(false); setCurrentAgent(''); }
    }
  }, [query, compareMode, conversation.length]);

  const hasConversation = conversation.length > 0;

  return (
    <div className="app">
      {/* ── Header ── */}
      <header className="header">
        <div className="header-inner">
          <a href="/" className="logo-link" onClick={e => { e.preventDefault(); setConversation([]); setQuery(''); setError(''); }}>
            <div className="logo-icon"><Shield size={22} /></div>
            <span className="logo-text">MedGuard</span>
          </a>
          <nav className="header-nav">
            <span className="nav-pill"><Activity size={12} /> Multi-Agent Pipeline</span>
            <span className="nav-pill"><ShieldCheck size={12} /> 8-Layer Verification</span>
            <button
              className={`compare-toggle ${compareMode ? 'active' : ''}`}
              onClick={() => setCompareMode(!compareMode)}
              title="Compare with raw LLM"
            >
              <Columns size={14} />
              <span>Compare</span>
            </button>
            {hasConversation && (
              <button className="new-chat-btn" onClick={() => { setConversation([]); setQuery(''); setError(''); }} title="New conversation">
                <MessageCircle size={14} />
                <span>New</span>
              </button>
            )}
          </nav>
        </div>
      </header>

      <main className={`main ${compareMode && hasConversation ? 'main-wide' : ''}`}>
        {/* ── Landing (no conversation yet) ── */}
        {!hasConversation && !loading && (
          <div className="landing">
            <div className="landing-logo">
              <div className="landing-icon-ring">
                <Shield size={44} />
              </div>
            </div>
            <h1 className="landing-title">MedGuard</h1>
            <p className="landing-sub">Self-correcting medical AI with real-time hallucination detection</p>
            {compareMode && (
              <div className="compare-active-badge">
                <Columns size={14} /> Compare Mode Active — results will show side-by-side with raw LLM
              </div>
            )}
          </div>
        )}

        {/* ── Initial Search Bar (only shown when no conversation) ── */}
        {!hasConversation && (
          <>
            <div className="search-wrap">
              <form onSubmit={e => { e.preventDefault(); handleSubmit(); }} className={`search-bar ${compareMode ? 'search-bar-compare' : ''}`}>
                <div className="search-left-ring" />
                <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)}
                  placeholder={compareMode ? "Ask a question to compare raw LLM vs MedGuard..." : "Ask a medical question..."} disabled={loading} className="search-input" />
                <VoiceInputButton onTranscript={handleVoiceTranscript} disabled={loading} />
                <button type="submit" disabled={loading || !query.trim()} className="search-btn">
                  {loading ? <Loader2 size={18} className="spinning" /> : <ArrowRight size={18} />}
                </button>
              </form>
            </div>

            {/* Quick Action Buttons */}
            <div className="quick-actions">
              {demoQuestions.slice(0, 3).map((dq, i) => (
                <button key={i} className="quick-btn" onClick={() => { setQuery(dq.text); handleSubmit(dq.text); }}>
                  <span className="quick-icon">{i === 0 ? '💊' : i === 1 ? '🔬' : '⚕️'}</span>
                  <span>{dq.category || dq.text}</span>
                </button>
              ))}
            </div>

            {/* Demo question cards */}
            {demoQuestions.length > 0 && (
              <div className="demo-section">
                <div className="demo-grid">
                  {demoQuestions.map((dq, i) => (
                    <button key={i} className="demo-card" onClick={() => { setQuery(dq.text); handleSubmit(dq.text); }}>
                      <span className="demo-cat">{dq.category}</span>
                      <span className="demo-q">{dq.text}</span>
                      <ArrowRight size={14} className="demo-arrow" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Error ── */}
        {error && <div className="error-box"><AlertTriangle size={16} /> {error}</div>}

        {/* ── Conversation Turns ── */}
        {hasConversation && (
          <div className="conversation">
            {/* Turn count indicator */}
            {conversation.length > 1 && (
              <div className="convo-indicator">
                <MessageCircle size={12} /> {conversation.length} questions in this session
              </div>
            )}

            {conversation.map((turn, idx) => {
              const isLatest = idx === conversation.length - 1;
              if (compareMode && turn.compareResult) {
                return <CompareTurn key={idx} turn={turn} isLatest={isLatest} />;
              }
              return (
                <ConversationTurn
                  key={idx}
                  turn={turn}
                  isLatest={isLatest}
                  isLoading={isLatest && loading}
                  currentAgent={currentAgent}
                />
              );
            })}

            {/* Loading state for compare mode on latest turn */}
            {compareMode && loading && !conversation[conversation.length - 1]?.compareResult && (
              <div className="compare-loading-box">
                <Loader2 size={24} className="spinning" />
                <div className="compare-loading-text">Running both raw LLM and MedGuard pipeline in parallel...</div>
                <div className="compare-loading-sub">This may take 30-60 seconds</div>
              </div>
            )}

            {/* Divider before follow-up */}
            {!loading && conversation[conversation.length - 1]?.result && (
              <div className="turn-divider" />
            )}

            {/* Follow-up bar — always visible when not loading */}
            {!loading && conversation.length > 0 && conversation[conversation.length - 1]?.result && (
              <form onSubmit={e => { e.preventDefault(); handleSubmit(); }} className="followup-bar">
                <div className="followup-ring" />
                <input ref={followupRef} type="text" value={query} onChange={e => setQuery(e.target.value)}
                  placeholder="Ask a follow-up question..." className="followup-input" />
                <VoiceInputButton onTranscript={handleVoiceTranscript} disabled={loading} />
                <button type="submit" disabled={loading || !query.trim()} className="followup-btn">
                  {loading ? <Loader2 size={16} className="spinning" /> : <ArrowRight size={16} />}
                </button>
              </form>
            )}

            <div ref={bottomRef} />
          </div>
        )}
      </main>

      <footer className="footer">
        <span>MedGuard</span> <span className="footer-dot">·</span> <span>HooHacks 2026</span>
        <span className="footer-dot">·</span> <span>LangGraph + Gemini + Cross-Encoder NLI</span>
      </footer>
    </div>
  );
}
